/**
 * Console header (§7.3), always visible:
 *  - bot identity
 *  - runtime state, never behind a menu
 *  - a rental banner while a rental is active, naming who holds it and how long
 *    is left
 *  - the owner's kill switch: one click plus one confirmation, never more
 *  - a persistent warning naming the account state and required action whenever
 *    health is not `healthy`
 */

import { useState } from 'react';

import { apiFetch } from '../../api/client';
import { toProblem, type Problem } from '../../api/problem';
import type { Bot, Rental, RuntimeState } from '../../api/types';
import type { CapabilityResolver } from '../../hooks/useCapabilities';
import { Gate } from '../../components/Gate';
import { ConfirmButton } from '../../components/ConfirmButton';
import { ErrorView } from '../../components/ErrorView';
import { ActionRequiredBanners } from '../../components/NotificationCentre';
import { RuntimeBadge, healthCopy, worstAccount } from '../../components/health';
import { DmReplyModeControl } from './DmReplyModeControl';
import { Banner } from '../../components/ui';
import { duration, localDateTime } from '../../lib/format';
import { OWNER_LABEL, RENTER_LABEL } from '../../lib/vocabulary';
import { routes } from '../../api/routes';

export function ConsoleHeader({
  bot,
  caps,
  reload,
  scope,
  rental,
}: {
  bot: Bot;
  caps: CapabilityResolver;
  reload: () => void;
  scope: 'owner' | 'renter';
  rental: Rental | null;
}) {
  const [problem, setProblem] = useState<Problem | null>(null);
  const [pending, setPending] = useState<RuntimeState | 'kill' | null>(null);

  const transition = async (path: 'run' | 'pause' | 'stop', marker: RuntimeState | 'kill') => {
    setPending(marker);
    setProblem(null);
    try {
      await apiFetch(routes.bots.transition(bot.id, path), { method: 'POST', body: {} });
      reload();
    } catch (e) {
      setProblem(toProblem(e));
    } finally {
      setPending(null);
    }
  };

  const unhealthy = worstAccount(bot.social_accounts);
  const active = bot.active_rental;

  return (
    <header className="console-header">
      <div className="console-identity">
        <div className="console-title">
          <h1>{bot.name}</h1>
          <RuntimeBadge state={bot.runtime_state} />
        </div>
        <p className="muted">
          @{bot.slug} · {OWNER_LABEL} @{bot.owner_handle} · {bot.timezone}
        </p>
      </div>

      <div className="console-controls">
        <Gate caps={caps} capability="run_stop">
          {(disabled) => (
            <button
              type="button"
              className="btn"
              disabled={disabled || bot.runtime_state === 'running' || pending !== null}
              onClick={() => void transition('run', 'running')}
            >
              Run
            </button>
          )}
        </Gate>

        <Gate caps={caps} capability="pause">
          {(disabled) => (
            <button
              type="button"
              className="btn"
              disabled={disabled || bot.runtime_state !== 'running' || pending !== null}
              onClick={() => void transition('pause', 'paused')}
            >
              Pause
            </button>
          )}
        </Gate>

        {/*
          The kill switch. It is a safety control, so it is never gated behind a
          fee, a delay, a reason field or a multi-step flow — one click, one
          confirmation. It stays enabled even when the bot is already stopped, so
          a panicking owner never has to work out whether it applies.
        */}
        {scope === 'owner' ? (
          <Gate caps={caps} capability="run_stop">
            {(disabled) => (
              <ConfirmButton
                label="Stop everything"
                confirmTitle="Stop this bot now?"
                confirmBody={
                  <p>
                    The bot stops immediately. Scheduled items will not send, and anything
                    mid-flight is abandoned. You can start it again whenever you want.
                  </p>
                }
                confirmLabel="Stop now"
                onConfirm={() => transition('stop', 'kill')}
                disabled={disabled}
                pending={pending === 'kill'}
              />
            )}
          </Gate>
        ) : (
          <Gate caps={caps} capability="run_stop">
            {(disabled) => (
              <button
                type="button"
                className="btn"
                disabled={disabled || bot.runtime_state === 'stopped' || pending !== null}
                onClick={() => void transition('stop', 'stopped')}
              >
                Stop
              </button>
            )}
          </Gate>
        )}
      </div>

      <DmReplyModeControl bot={bot} caps={caps} reload={reload} />

      {problem ? <ErrorView problem={problem} onRetry={reload} /> : null}

      {active ? (
        <Banner
          tone="info"
          title={`Rental in progress — ${RENTER_LABEL} @${active.renter_handle}`}
        >
          {duration(active.ends_in_seconds)} remaining. Ends {localDateTime(active.end_at)}.
        </Banner>
      ) : null}

      {unhealthy ? (
        <Banner
          tone={healthCopy(unhealthy.status).tone === 'bad' ? 'bad' : 'warn'}
          title={`${unhealthy.platform} account @${unhealthy.external_handle}: ${healthCopy(unhealthy.status).state}`}
          role="alert"
          actions={
            unhealthy.status === 'needs_reauth' || unhealthy.status === 'revoked' ? (
              <a className="btn btn-primary btn-sm" href={`/bots/${bot.id}/accounts`}>
                Re-authorize
              </a>
            ) : undefined
          }
        >
          {healthCopy(unhealthy.status).action}
        </Banner>
      ) : null}

      <ActionRequiredBanners
        scope={rental ? { botId: bot.id, rentalId: rental.id } : { botId: bot.id }}
      />
    </header>
  );
}
